/* eslint-disable no-console */
import type { StatusType } from '@datadog/browser-logs';
import { datadogLogs } from '@datadog/browser-logs';
import type { Debugger } from 'debug';
import debug from 'debug';

/*
  Usage:-
    logs will show in development only, in production logs will be disabled
    to turn on in production set localStorage.debug = 'MG*';

  import {Logger} from '~/lib/logger';
  const logger = new Logger('MyComponent');
  logger.info(`hello world ${someValue}`);
*/

const NAMESPACE = 'MG';

if (process.env.NODE_ENV === 'development') {
  debug.enable(`${NAMESPACE}*`);
}

export class Logger {
  _debug: Debugger;
  _info: Debugger;
  _warn: Debugger;
  _error: Debugger;

  prefix?: string;
  enableDDogBrowserLogs: boolean;

  static getUserIdFunc: (() => string | undefined) | undefined;

  constructor(prefix?: string | undefined, enableDatadogLogs: boolean = process.env.NODE_ENV !== 'development') {
    if (prefix) {
      this._debug = debug(`${NAMESPACE}:${prefix}`);
      this._info = debug(`${NAMESPACE}:INFO:${prefix}`);
      this._warn = debug(`${NAMESPACE}:WARN:${prefix}`);
      this._error = debug(`${NAMESPACE}:ERROR:${prefix}`);
    } else {
      this._debug = debug(`${NAMESPACE}`);
      this._info = debug(`${NAMESPACE}:INFO`);
      this._warn = debug(`${NAMESPACE}:WARN`);
      this._error = debug(`${NAMESPACE}:ERROR`);
    }

    this.enableDDogBrowserLogs = enableDatadogLogs;
    this.prefix = prefix;

    this._debug.log = function (...args: unknown[]) {
      console.debug(...args);
    };
    this._info.log = function (...args: unknown[]) {
      console.info(...args);
    };
    this._warn.log = function (...args: unknown[]) {
      console.warn(...args);
    };
    this._error.log = function (...args: unknown[]) {
      console.error(...args);
    };
  }

  debug(...args: unknown[]) {
    this.wrapLog(this._debug, 'debug', ...args);
  }

  info(...args: unknown[]) {
    this.wrapLog(this._info, 'info', ...args);
  }

  warn(...args: unknown[]) {
    this.wrapLog(this._warn, 'warn', ...args);
  }

  error(...args: unknown[]) {
    this.wrapLog(this._error, 'error', ...args);
  }

  static getUserId(): string | undefined {
    if (Logger.getUserIdFunc) {
      return Logger.getUserIdFunc();
    }
    return undefined;
  }

  sendToDatadog(logLevel: StatusType, ...args: unknown[]): void {
    try {
      const first = args?.[0];
      const message = typeof first === 'string' ? first : JSON.stringify(first);

      const userId = Logger.getUserId() || 'logged_out';
      const err = args.find((x) => x instanceof Error) as Error | undefined;

      datadogLogs.logger.log(
        [NAMESPACE, this.prefix, message].filter(Boolean).join(':'),
        { args, user_id: userId },
        logLevel,
        err,
      );
    } catch (err) {
      console.error('Failed to send to datadog: ', err);
    }
  }

  wrapLog(logFunc: (...args: unknown[]) => void, logLevel: StatusType, ...args: unknown[]) {
    logFunc(...args);
    if (this.enableDDogBrowserLogs && logLevel !== 'debug') {
      this.sendToDatadog(logLevel, ...args);
    }
  }
}
